
import React from 'react';
import { CheckCircle, Clock, AlertCircle, Mail } from 'lucide-react';

interface EmailStatusIndicatorProps {
  status: 'sending' | 'sent' | 'error' | 'confirmed';
  email: string;
}

export const EmailStatusIndicator: React.FC<EmailStatusIndicatorProps> = ({ status, email }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'sending':
        return {
          icon: Clock,
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          title: 'Sending confirmation email...',
          description: 'Please wait while we send your confirmation email.'
        };
      case 'sent':
        return {
          icon: Mail,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          title: 'Confirmation email sent!',
          description: `We've sent a confirmation link to ${email}`
        };
      case 'error':
        return {
          icon: AlertCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          title: 'Failed to send email',
          description: 'There was an issue sending your confirmation email. Please try again.'
        };
      case 'confirmed':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          title: 'Email confirmed!',
          description: 'Your account has been verified. You can now sign in.'
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  const Icon = config.icon;

  return (
    <div className={`${config.bgColor} ${config.borderColor} border rounded-lg p-4 mb-6`}>
      <div className="flex items-start space-x-3">
        <Icon className={`h-6 w-6 ${config.color} mt-0.5 flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-medium ${config.color}`}>
            {config.title}
          </h3>
          <p className="text-sm text-gray-700 mt-1">
            {config.description}
          </p>
        </div>
      </div>
    </div>
  );
};
